import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const I18nContext = createContext(null);
export const useI18n = () => useContext(I18nContext);
export const LANG_RE = /^\/([a-z]{2,3})(?=\/|$)/;

export function detectLang(codes) {
  const saved = localStorage.getItem("bg_lang");
  if (saved && codes.includes(saved)) return saved;
  for (const l of navigator.languages || [navigator.language || "en"]) {
    const short = l.toLowerCase().split("-")[0];
    if (codes.includes(short)) return short;
  }
  return codes.includes("en") ? "en" : codes[0] || "en";
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState(() => location.pathname.match(LANG_RE)?.[1] || localStorage.getItem("bg_lang") || "en");
  const [data, setData] = useState({ languages: [], strings: {} });
  const [legal, setLegal] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [audioAvailability, setAudioAvailability] = useState(null);

  useEffect(() => {
    api.get("/public/ui").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/legal").then((r) => setLegal(r.data)).catch(() => {});
  }, []);

  const setLang = useCallback((code) => {
    setLangState(code); localStorage.setItem("bg_lang", code);
    if (LANG_RE.test(location.pathname)) navigate(location.pathname.replace(LANG_RE, `/${code}`) + location.search, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const syncLangFromUrl = useCallback((code) => { setLangState(code); localStorage.setItem("bg_lang", code); }, []);

  const t = useCallback((key, vars) => {
    let s = data.strings?.[lang]?.[key] ?? data.strings?.en?.[key] ?? key;
    if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.split(`{${k}}`).join(String(v)); });
    return s;
  }, [data, lang]);

  const nativeName = useCallback((l) => l.native_name || l.name, []);
  const langName = useMemo(() => { const l = data.languages.find((x) => x.code === lang); return l ? nativeName(l) : lang.toUpperCase(); }, [data, lang, nativeName]);
  const legalText = useCallback((key) => { const c = legal?.[key]?.content || {}; return (c[lang] || c.en || "").trim(); }, [legal, lang]);
  const prefix = `/${lang}`;

  const value = useMemo(() => ({ lang, prefix, setLang, syncLangFromUrl, t, langName, nativeName, languages: data.languages, loaded: data.languages.length > 0, legal, legalText, sheetOpen, audioAvailability, setAudioAvailability,
    openLanguageSheet: () => setSheetOpen(true), closeLanguageSheet: () => setSheetOpen(false) }),
    [lang, prefix, setLang, syncLangFromUrl, t, langName, nativeName, data.languages, legal, legalText, sheetOpen, audioAvailability]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
